import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { Plus, Trash2 } from "lucide-react";

const SEVERITIES = ["HARD_STOP", "DEVIATION", "WARNING"];
const OPS = ["gte", "lte", "gt", "lt", "eq", "neq"];
const APPLIES = ["all", "CC", "TL", "MUDRA_SHISHU", "MUDRA_KISHORE", "MUDRA_TARUN", "PERSONAL_LOAN", "HOUSING_LOAN", "STAND_UP_INDIA", "WC_NAYAK", "CGTMSE_HYBRID", "SME_CC", "PROJECT"];

export default function PolicyRulesPage() {
  const { user, bank } = useAuth();
  const bankShort = bank?.short_name || "Bank";
  const canManage = user?.role === "HO";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", applies_to: "all", path: "", op: "gte", value: "", severity: "DEVIATION", message: "", active: true });
  const [busy, setBusy] = useState(false);

  const load = async () => { const r = await api.get("/policy/rules"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const add = async () => {
    if (!form.name || !form.path || !form.message) return toast.error("Name, path, message required");
    setBusy(true);
    try {
      const v = isNaN(Number(form.value)) ? form.value : Number(form.value);
      await api.post("/policy/rules", { ...form, value: v });
      toast.success("Rule added");
      setForm({ name: "", applies_to: "all", path: "", op: "gte", value: "", severity: "DEVIATION", message: "", active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const toggle = async (r) => { try { await api.patch(`/policy/rules/${r.id}`, { active: !r.active }); load(); } catch (e) { toast.error("Failed"); } };
  const remove = async (id) => { if (!window.confirm("Delete rule?")) return; await api.delete(`/policy/rules/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-3">
      <SectionHeader title={`${bankShort} Policy Rules`} subtitle="Configure rules that auto-flag deviations on every application." />
      {canManage && (
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-3">Add Rule</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="rule-name-input" /></Field>
            <Field label="Applies To">
              <Select value={form.applies_to} onValueChange={(v) => set("applies_to", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{APPLIES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Path"><Input value={form.path} onChange={(e) => set("path", e.target.value)} placeholder="e.g., cma.ratios.dscr[-1]" /></Field>
            <Field label="Operator">
              <Select value={form.op} onValueChange={(v) => set("op", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{OPS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Threshold Value"><Input value={form.value} onChange={(e) => set("value", e.target.value)} /></Field>
            <Field label="Severity">
              <Select value={form.severity} onValueChange={(v) => set("severity", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SEVERITIES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Message" className="md:col-span-2"><Input value={form.message} onChange={(e) => set("message", e.target.value)} /></Field>
            <div className="md:col-span-4 flex justify-end"><Button onClick={add} disabled={busy} data-testid="rule-add-button"><Plus className="h-4 w-4 mr-1" />Add Rule</Button></div>
          </div>
        </Card>
      )}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2.5">Name</th><th className="text-left px-3 py-2.5">Applies</th><th className="text-left px-3 py-2.5">Condition</th><th className="text-left px-3 py-2.5">Severity</th><th className="text-left px-3 py-2.5">Message</th><th className="text-left px-3 py-2.5">Active</th><th></th></tr></thead>
            <tbody>
              {items.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 text-xs"><Badge variant="secondary">{r.applies_to}</Badge></td>
                  <td className="px-3 py-2 mono text-[11px] text-muted-foreground">{r.path} {r.op} {String(r.value)}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={r.severity === "HARD_STOP" ? "border-destructive/40 text-destructive" : r.severity === "DEVIATION" ? "border-warning/40 text-warning" : "border-info/40 text-info"}>{r.severity}</Badge></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.message}</td>
                  <td className="px-3 py-2"><Switch checked={!!r.active} onCheckedChange={() => toggle(r)} disabled={!canManage} /></td>
                  <td className="px-3 py-2 text-right">{canManage && <Button size="sm" variant="outline" onClick={() => remove(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (<div className={`space-y-1.5 ${className}`}><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>);
}
